<!-- lib/components/Header.svelte -->
<script>
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import { isReferralPortal, APP_URL } from "utils/menu.js";
  import LanguageSwitcher from "./LanguageSwitcher.svelte";

  export let showLanguageSwitcher = true;

  let mobileMenuOpen = false;
  let aboutDropdown;
  let servicesDropdown;
  let dropdownCloseTimeoutId;

  const feesMenuItem = { labelKey: "header.menu.fees", link: "/docs/fees" };

  const aboutMenuItems = [
    { labelKey: "header.menu.aboutUs", link: "/docs/overview" },
    { labelKey: "header.menu.faq", link: "/docs/most-common" },
    { labelKey: "header.menu.contact", link: "/contact-us/" },
  ];

  const servicesMenuItems = [
    { labelKey: "header.menu.livePricing", link: "/live-pricing" },
    { labelKey: "header.menu.giftCards", link: "/gift-cards" },
    { labelKey: "header.menu.bullionAccount", link: "/register" },
  ];

  $: signInMenuItem = !isReferralPortal()
    ? { labelKey: "header.menu.signIn", link: APP_URL }
    : null;

  $: mobileMenuItems = [
    feesMenuItem,
    ...aboutMenuItems,
    ...servicesMenuItems,
    ...(signInMenuItem ? [signInMenuItem] : []),
  ];

  function openDropdown(activeDropdown) {
    if (dropdownCloseTimeoutId) {
      clearTimeout(dropdownCloseTimeoutId);
      dropdownCloseTimeoutId = null;
    }

    const dropdowns = [aboutDropdown, servicesDropdown];
    for (const dropdown of dropdowns) {
      if (dropdown && dropdown !== activeDropdown) {
        dropdown.open = false;
      }
    }

    if (activeDropdown) {
      activeDropdown.open = true;
    }
  }

  function closeDropdown(activeDropdown) {
    dropdownCloseTimeoutId = setTimeout(() => {
      if (activeDropdown) {
        activeDropdown.open = false;
      }
      dropdownCloseTimeoutId = null;
    }, 240);
  }

  function toggleMobileMenu() {
    mobileMenuOpen = !mobileMenuOpen;
  }

  function closeMobileMenu() {
    mobileMenuOpen = false;
  }

  onMount(() => {
    function handleClickOutside(event) {
      const header = document.querySelector("header");
      if (header && !header.contains(event.target)) {
        mobileMenuOpen = false;
      }
    }

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      if (dropdownCloseTimeoutId) {
        clearTimeout(dropdownCloseTimeoutId);
      }
    };
  });
</script>

<header>
  <div class="header-content">
    <a href="/" class="logo" aria-label={$_("header.logo.homeLink")}>
      <img
        src="/images/v2/LQN_logo_round.svg"
        alt={$_("header.logo.alt")}
        width="40"
        height="40"
      />
      Liquid Noble
    </a>

    <div class="header-controls">
      <nav class="nav">
        <a href={feesMenuItem.link} class="nav-link"
          >{$_(feesMenuItem.labelKey)}</a
        >

        <details
          class="dropdown"
          bind:this={aboutDropdown}
          on:mouseenter={() => openDropdown(aboutDropdown)}
          on:mouseleave={() => closeDropdown(aboutDropdown)}
        >
          <summary class="dropdown-toggle">
            {$_("header.menu.about")}
            <span class="dropdown-caret" aria-hidden="true"></span>
          </summary>
          <div class="dropdown-menu">
            {#each aboutMenuItems as menuItem}
              <a href={menuItem.link} class="dropdown-item"
                >{$_(menuItem.labelKey)}</a
              >
            {/each}
          </div>
        </details>

        <details
          class="dropdown"
          bind:this={servicesDropdown}
          on:mouseenter={() => openDropdown(servicesDropdown)}
          on:mouseleave={() => closeDropdown(servicesDropdown)}
        >
          <summary class="dropdown-toggle">
            {$_("header.menu.services")}
            <span class="dropdown-caret" aria-hidden="true"></span>
          </summary>
          <div class="dropdown-menu">
            {#each servicesMenuItems as menuItem}
              <a href={menuItem.link} class="dropdown-item"
                >{$_(menuItem.labelKey)}</a
              >
            {/each}
          </div>
        </details>

        {#if signInMenuItem}
          <a href={signInMenuItem.link} class="nav-link sign-in-cta"
            >{$_(signInMenuItem.labelKey)}</a
          >
        {/if}
      </nav>

      {#if showLanguageSwitcher}
        <LanguageSwitcher />
      {/if}

      <button
        class="mobile-menu-toggle"
        class:open={mobileMenuOpen}
        on:click={toggleMobileMenu}
        aria-label={$_("header.mobile.toggleMenu")}
      >
        <span class="hamburger"></span>
        <span class="hamburger"></span>
        <span class="hamburger"></span>
      </button>
    </div>
  </div>

  <div class="mobile-nav" class:open={mobileMenuOpen}>
    {#each mobileMenuItems as menuItem}
      <a href={menuItem.link} on:click={closeMobileMenu}
        >{$_(menuItem.labelKey)}</a
      >
    {/each}
    {#if showLanguageSwitcher}
      <div class="mobile-language-switcher">
        <LanguageSwitcher />
      </div>
    {/if}
  </div>
</header>

<style lang="scss">
  @keyframes goldShine {
    0% {
      background-position: -200% 0;
    }
    60% {
      background-position: 200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  header {
    background-color: var(--card-bg);
    border-bottom: 1px solid #260d28;
    width: 100%;
    position: relative;
    z-index: 100; // Lower than document overlays
  }

  .header-content {
    max-width: var(--max-width);
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 3rem;
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.5rem;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.3s ease;
    background: linear-gradient(
      120deg,
      #c6a858 20%,
      #fceabb 40%,
      #dbbb6b 50%,
      #fceabb 60%,
      #c6a858 80%
    );
    background-size: 200% auto;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: goldShine 5s ease-in-out infinite;

    &:hover {
      transform: translateY(-1px);
      filter: brightness(1.1);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .header-controls {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  .nav {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  .nav-link,
  .dropdown-toggle {
    color: var(--text-color3);
    text-decoration: none;
    font-weight: 500;
    font-size: 1rem;
    transition: all 0.3s ease;
    position: relative;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    list-style: none;

    &:hover {
      color: #fff;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: var(--text-color1);
      transition: width 0.3s ease;
    }

    &:hover::after {
      width: 100%;
    }
  }

  .dropdown {
    position: relative;
  }

  .dropdown summary::-webkit-details-marker {
    display: none;
  }

  .dropdown-caret {
    width: 0.4rem;
    height: 0.4rem;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    opacity: 0.8;
    transform: rotate(45deg) translateY(-1px);
    transition:
      transform 0.2s ease,
      opacity 0.2s ease;
  }

  .dropdown[open] .dropdown-caret {
    opacity: 1;
    transform: rotate(225deg) translateY(0);
  }

  .dropdown-menu {
    position: absolute;
    top: calc(100% + 0.85rem);
    left: 0;
    min-width: 12rem;
    display: flex;
    flex-direction: column;
    background: #1e0222;
    border: 1px solid rgba(198, 168, 88, 0.28);
    border-radius: 0.75rem;
    box-shadow: 0 16px 28px rgba(0, 0, 0, 0.35);
    padding: 0.5rem;
    z-index: 300;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: -0.85rem;
      height: 0.4rem;
    }
  }

  .dropdown-item {
    color: var(--text-color3);
    text-decoration: none;
    font-weight: 500;
    font-size: 0.95rem;
    padding: 0.65rem 0.75rem;
    border-radius: 0.5rem;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    &:hover {
      color: #fff;
      background-color: rgba(255, 255, 255, 0.08);
    }
  }

  .sign-in-cta {
    color: #2d0b24;
    background: linear-gradient(135deg, #f7da79, #e2b83f);
    border: 1px solid rgba(255, 231, 152, 0.85);
    border-radius: 999px;
    padding: 0.5rem 0.9rem;
    font-weight: 700;
    box-shadow: 0 8px 20px rgba(199, 148, 32, 0.25);

    &::after {
      display: none;
    }

    &:hover {
      color: #22091f;
      filter: brightness(1.05);
      transform: translateY(-1px);
      box-shadow: 0 10px 24px rgba(199, 148, 32, 0.35);
    }
  }

  .mobile-menu-toggle {
    display: none;
    flex-direction: column;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    gap: 0.25rem;
    z-index: 101; // Slightly higher than header but lower than document overlays
  }

  .hamburger {
    width: 24px;
    height: 3px;
    background-color: var(--text-color3);
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
  }

  .mobile-menu-toggle.open .hamburger:nth-child(1) {
    transform: rotate(45deg) translate(6px, 6px);
  }

  .mobile-menu-toggle.open .hamburger:nth-child(2) {
    opacity: 0;
  }

  .mobile-menu-toggle.open .hamburger:nth-child(3) {
    transform: rotate(-45deg) translate(6px, -6px);
  }

  .mobile-nav {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--card-bg);
    border-bottom: 1px solid #260d28;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    z-index: 500; // Higher than header but lower than document overlays
    opacity: 0;
    transform: translateY(-10px);
    transition: all 0.3s ease;

    &.open {
      display: block;
      opacity: 1;
      transform: translateY(0);
    }
  }

  .mobile-nav a {
    display: block;
    color: var(--text-color3);
    text-decoration: none;
    font-weight: 500;
    font-size: 1.1rem;
    padding: 1.25rem 3rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
      color: #fff;
      padding-left: 3.5rem;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  .mobile-language-switcher {
    padding: 1.25rem 3rem;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }

  @media (max-width: 1024px) {
    .header-content {
      padding: 1.25rem 2rem;
    }

    .nav {
      gap: 1rem;
    }

    .nav-link,
    .dropdown-toggle {
      font-size: 0.95rem;
    }
  }

  @media (max-width: 768px) {
    .nav {
      display: none;
    }

    .mobile-menu-toggle {
      display: flex;
    }

    .header-content {
      padding: 1rem 2rem;
    }

    .header-controls {
      gap: 1rem;
    }
  }

  @media (max-width: 480px) {
    .header-content {
      padding: 1rem 1.5rem;
    }

    .logo {
      font-size: 1.25rem;
    }

    .logo img {
      width: 32px;
      height: 32px;
    }

    .mobile-nav a {
      padding: 1rem 1.5rem;
      font-size: 1rem;

      &:hover {
        padding-left: 2rem;
      }
    }

    .mobile-language-switcher {
      padding: 1rem 1.5rem;
    }
  }

  @media (max-width: 320px) {
    .header-content {
      padding: 0.75rem 1rem;
    }

    .logo {
      font-size: 1.1rem;
      gap: 0.5rem;
    }

    .logo img {
      width: 28px;
      height: 28px;
    }
  }
</style>
